import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FaArrowLeft, FaEye } from "react-icons/fa";
import { reportsApi } from "../../api/reportsApi";
import { getAllGoods } from "../../api/goodsApi";
import { useNavigate } from "react-router-dom";
import AppDropdown from "../../components/AppDropdown";
import backgroundPattern from '../../assets/pasbookpng.png';
import "../../style/ReportsLayout.css";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Pagination,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";

const Reports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("productwise");
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [backgroundSize, setBackgroundSize] = useState('220px');

  // Responsive background size
  useEffect(() => {
    const updateBackgroundSize = () => {
      if (window.innerWidth <= 480) {
        setBackgroundSize('120px');
      } else if (window.innerWidth <= 768) {
        setBackgroundSize('150px');
      } else {
        setBackgroundSize('220px');
      }
    };

    updateBackgroundSize();
    window.addEventListener('resize', updateBackgroundSize);
    return () => window.removeEventListener('resize', updateBackgroundSize);
  }, []);


  // Product Wise Report State
  const [productWiseData, setProductWiseData] = useState([]);
  const [productWiseParams, setProductWiseParams] = useState({
    fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0]
  });
  const [productWisePage, setProductWisePage] = useState(1);
  const [productWisePageSize, setProductWisePageSize] = useState(10);
  // eslint-disable-next-line no-unused-vars
  const [productWiseTotalPages, setProductWiseTotalPages] = useState(1);

  // Product Wise Inventory State
  const [inventoryData, setInventoryData] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [goods, setGoods] = useState([]);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [inventoryPageSize, setInventoryPageSize] = useState(10);
  const [inventoryTotalPages, setInventoryTotalPages] = useState(0);
  const [inventoryTotalElements, setInventoryTotalElements] = useState(0);
  const [dateSortOrder, setDateSortOrder] = useState("desc");
  const [inventorySummary, setInventorySummary] = useState({
    uniqueCustomers: 0,
    totalChallans: 0,
    deliveredQty: 0,
    receivedQty: 0,
    netQty: 0
  });
  const [inventoryParams, setInventoryParams] = useState({
    fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0]
  });

  // Fetch goods on mount
  useEffect(() => {
    const fetchGoods = async () => {
      try {
        const response = await getAllGoods();
        console.log("Goods API response:", response.data);
        console.log("Goods response structure:", typeof response.data);
        
        // Handle different possible response structures
        let goodsList = [];
        if (response.data?.data && Array.isArray(response.data.data)) {
          goodsList = response.data.data;
        } else if (response.data?.content && Array.isArray(response.data.content)) {
          goodsList = response.data.content;
        } else if (Array.isArray(response.data)) {
          goodsList = response.data;
        } else if (response.data?.items && Array.isArray(response.data.items)) {
          goodsList = response.data.items;
        }
        
        console.log("Extracted goods list:", goodsList);
        console.log("Number of goods:", goodsList.length);
        
        setGoods(goodsList);
      } catch (error) {
        console.error("Error fetching goods:", error);
      }
    };
    fetchGoods();
  }, []);

  // Fetch inventory when page changes (only if product is selected)
  useEffect(() => {
    if (selectedProduct && activeTab === "inventory") {
      fetchProductInventory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryPage, inventoryPageSize]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // Product Wise Report Functions
  const fetchProductWiseReport = async () => {
    setLoading(true);
    try {
      const params = { ...productWiseParams };
      console.log("Fetching product wise report with params:", params);
      const response = await reportsApi.getProductWiseReport(params);
      console.log("Product wise report response:", response);
      console.log("Product wise report data:", response.data);
      
      // Handle different response structures
      let reportData = [];
      if (Array.isArray(response.data)) {
        reportData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        reportData = response.data.data;
      } else if (response.data?.content && Array.isArray(response.data.content)) {
        reportData = response.data.content;
      }
      
      console.log("Extracted product wise data:", reportData);
      setProductWiseData(reportData);
      
      if (reportData.length === 0) {
        toast.info("No data found for the selected period");
      }
    } catch (error) {
      console.error("Error fetching product wise report:", error);
      toast.error(error.response?.data?.message || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const handleProductWiseDateChange = (field, value) => {
    setProductWiseParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductWisePeriod = (period) => {
    const today = new Date();
    let fromDate = new Date();

    if (period === "WEEKLY") {
      fromDate.setDate(today.getDate() - 7);
    } else if (period === "MONTHLY") {
      fromDate.setMonth(today.getMonth() - 1);
    }

    setProductWiseParams({
      fromDate: fromDate.toISOString().split("T")[0],
      toDate: today.toISOString().split("T")[0]
    });
  };

  const handleProductWisePageSizeChange = (newSize) => {
    setProductWisePageSize(newSize);
    setProductWisePage(1);
  };

  // Product Wise Inventory Functions
  const fetchProductInventory = async (productNameOverride = null) => {
    console.log("=== fetchProductInventory START ===");
    console.log("productNameOverride:", productNameOverride);
    console.log("productNameOverride type:", typeof productNameOverride);
    console.log("selectedProduct state:", selectedProduct);
    console.log("selectedProduct type:", typeof selectedProduct);
    
    // Check if productNameOverride is a React SyntheticBaseEvent (click event)
    // React events have a nativeEvent property
    let actualProductName = productNameOverride;
    if (productNameOverride && typeof productNameOverride === 'object' && productNameOverride.nativeEvent) {
      console.log("WARNING: React SyntheticBaseEvent detected, discarding and using selectedProduct");
      actualProductName = null;
    }
    
    const productName = actualProductName || selectedProduct;
    console.log("Final productName to use:", productName);
    console.log("Final productName type:", typeof productName);
    
    if (!productName || productName === "") {
      console.error("ERROR: productName is empty or null");
      toast.error("Please select a product");
      return;
    }

    // Ensure productName is a string
    const productNameStr = typeof productName === 'string' ? productName : String(productName);
    console.log("productNameStr:", productNameStr);
    console.log("inventoryParams:", inventoryParams);
    console.log("inventoryPage:", inventoryPage);
    console.log("inventoryPageSize:", inventoryPageSize);

    setLoading(true);
    try {
      const params = {
        ...inventoryParams,
        productName: productNameStr,
        page: inventoryPage - 1, // Convert to 0-based for backend
        size: inventoryPageSize,
        sort: `date,${dateSortOrder}` // Add date sort parameter
      };

      console.log("API params:", params);
      const response = await reportsApi.getProductInventoryReport(params);
      console.log("API Response:", response);
      console.log("response.data:", response.data);

      // Handle new response structure with backend summary
      if (response.data && response.data.data) {
        const pageData = response.data.data;
        const summary = response.data.summary;

        setInventoryData(pageData.content || []);
        setInventoryTotalPages(pageData.totalPages || 0);
        setInventoryTotalElements(pageData.totalElements || 0);

        if (summary) {
          setInventorySummary({
            uniqueCustomers: summary.uniqueCustomers || 0,
            totalChallans: summary.totalChallans || 0,
            deliveredQty: summary.deliveredQty || 0,
            receivedQty: summary.receivedQty || 0,
            netQty: summary.netQty || 0
          });
        } else {
          // Fallback to current page calculation if summary not provided
          const deliveredTotal = (pageData.content || []).reduce((sum, item) => {
            return sum + (item.deliveredQty || 0) + (item.combinedDeliveredQty || 0);
          }, 0);
          const receivedTotal = (pageData.content || []).reduce((sum, item) => {
            return sum + (item.receivedQty || 0) + (item.combinedReceivedQty || 0);
          }, 0);

          setInventorySummary({
            uniqueCustomers: new Set((pageData.content || []).map(item => item.customerName)).size,
            totalChallans: (pageData.content || []).length,
            deliveredQty: deliveredTotal,
            receivedQty: receivedTotal,
            netQty: deliveredTotal - receivedTotal
          });
        }
      } else {
        // Handle legacy response structure (fallback)
        const data = response.data;
        setInventoryData(data.content || data || []);
        setInventoryTotalPages(data.totalPages || 0);
        setInventoryTotalElements(data.totalElements || data?.length || 0);

        const content = data.content || data || [];
        const deliveredTotal = content.reduce((sum, item) => {
          return sum + (item.deliveredQty || 0) + (item.combinedDeliveredQty || 0);
        }, 0);
        const receivedTotal = content.reduce((sum, item) => {
          return sum + (item.receivedQty || 0) + (item.combinedReceivedQty || 0);
        }, 0);

        setInventorySummary({
          uniqueCustomers: new Set(content.map(item => item.customerName)).size,
          totalChallans: content.length,
          deliveredQty: deliveredTotal,
          receivedQty: receivedTotal,
          netQty: deliveredTotal - receivedTotal
        });
      }

      const content = response.data?.data?.content || response.data?.content || response.data || [];
      if (content.length === 0) {
        toast.info("No inventory data found for the selected period");
      }
    } catch (error) {
      console.error("Error fetching product inventory:", error);
      toast.error(error.response?.data?.message || "Failed to fetch inventory");
      setInventoryData([]);
      setInventoryTotalPages(0);
      setInventoryTotalElements(0);
      setInventorySummary({
        uniqueCustomers: 0,
        totalChallans: 0,
        deliveredQty: 0,
        receivedQty: 0,
        netQty: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInventoryDateChange = (field, value) => {
    setInventoryParams(prev => ({
      ...prev,
      [field]: value
    }));
    setInventoryPage(1);
  };

  const handleInventoryPeriod = (period) => {
    const today = new Date();
    let fromDate = new Date();

    if (period === "WEEKLY") {
      fromDate.setDate(today.getDate() - 7);
    } else if (period === "MONTHLY") {
      fromDate.setMonth(today.getMonth() - 1);
    }

    setInventoryParams(prev => ({
      ...prev,
      fromDate: fromDate.toISOString().split("T")[0],
      toDate: today.toISOString().split("T")[0]
    }));
    setInventoryPage(1);
  };

  const handleInventoryPageSizeChange = (newSize) => {
    setInventoryPageSize(newSize);
    setInventoryPage(1);
  };


  const handleViewProduct = (productName) => {
    setSelectedProduct(productName);
    setInventoryPage(1);
    setInventoryParams({
      fromDate: productWiseParams.fromDate,
      toDate: productWiseParams.toDate
    });
    setActiveTab("inventory");
    // Auto-trigger fetch after tab switch
    setTimeout(() => {
      fetchProductInventory(productName);
    }, 100);
  };

  const getNetQty = (item) => {
    let deliveredQty = item.deliveredQty || 0;
    let receivedQty = item.receivedQty || 0;
    let combinedDelivered = item.combinedDeliveredQty || 0;
    let combinedReceived = item.combinedReceivedQty || 0;

    const totalDelivered = deliveredQty + combinedDelivered;
    const totalReceived = receivedQty + combinedReceived;
    return totalDelivered - totalReceived;
  };


  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      toast.error("No data to export");
      return;
    }

    let csv = "";
    if (activeTab === "productwise") {
      csv = "Product Name,Delivered Qty,Received Qty,Customers\n";
      data.forEach(item => {
        csv += `"${item.productName}",${item.deliveredQty},${item.receivedQty},${item.uniqueCustomers || 0}\n`;
      });
    } else {
      csv = "Date,Customer Name,Challan No,Challan Type,Delivered Qty,Received Qty,Combined Delivered,Combined Received,Net Qty\n";
      data.forEach(item => {
        csv += `${formatDate(item.date)},"${item.customerName}","${item.challanNo}","${item.challanType}",${item.deliveredQty},${item.receivedQty},${item.combinedDeliveredQty},${item.combinedReceivedQty},${getNetQty(item)}\n`;
      });
    }

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success("Report exported successfully");
  };

  return (
    <div className="reports-page" style={{ position: 'relative', padding: '8px 4px 36px', minHeight: '100vh', background: 'var(--page-bg)' }}>
      <div
        style={{
          position: 'fixed',
          inset: '-30%',
          backgroundImage: `url(${backgroundPattern})`,
          backgroundRepeat: 'repeat',
          backgroundSize: backgroundSize,
          transform: 'rotate(-12deg)',
          opacity: 0.08,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            background: '#4338ca',
            borderRadius: '12px',
            color: 'white',
            flexShrink: 0
          }}
        >
          <svg 
            style={{ 
              fontSize: 24, 
              color: 'white',
              width: 24, 
              height: 24,
              userSelect: 'none',
              display: 'inline-block',
              fill: 'currentColor'
            }} 
            viewBox="0 0 24 24" 
            focusable="false" 
            aria-hidden="true"
          >
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"></path>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px 0', lineHeight: 1.2 }}>Reports</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>View and generate various business reports</p>
        </div>
        <button onClick={() => navigate(-1)} style={{ background: 'var(--medium-grey)', border: 'none', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '18px', transition: 'all 0.3s ease', color: 'var(--text-secondary)' }}>
          <FaArrowLeft />
        </button>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border-color)', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveTab("productwise")}
          style={{ padding: '16px 24px', background: 'none', border: 'none', fontSize: '16px', fontWeight: '600', color: activeTab === "productwise" ? 'var(--primary-color)' : 'var(--text-secondary)', cursor: 'pointer', transition: 'all 0.3s ease', textTransform: 'capitalize', position: 'relative' }}
        >
          Product Wise Report
        </button>
        <button
          onClick={() => setActiveTab("inventory")}
          style={{ padding: '16px 24px', background: 'none', border: 'none', fontSize: '16px', fontWeight: '600', color: activeTab === "inventory" ? 'var(--primary-color)' : 'var(--text-secondary)', cursor: 'pointer', transition: 'all 0.3s ease', textTransform: 'capitalize', position: 'relative' }}
        >
          Product Wise Inventory
        </button>
      </div>

      {/* Product Wise Report Tab */}
      {activeTab === "productwise" && (
        <div>
          {/* Filters */}
          {!filtersOpen ? (
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setFiltersOpen(true)}
              sx={{
                mb: 3,
                height: 48,
                borderRadius: "10px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "14px",
                borderColor: 'var(--border-color)',
                color: 'var(--text-secondary)',
                "&:hover": {
                  borderColor: 'var(--primary-color)',
                  background: 'var(--primary-soft)',
                  color: 'var(--primary-color)',
                },
              }}
            >
              Show Filters
            </Button>
          ) : (
            <Card
              sx={{
                mb: 2,
                borderRadius: "12px",
                border: '1px solid var(--border-color)',
                boxShadow: "0 4px 16px rgba(15,23,42,.05)",
                overflow: "visible",
                background: 'var(--card-bg)',
                position: "relative",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <CardContent
                sx={{
                  px: { xs: 1.5, sm: 2, md: 2 },
                  py: { xs: 1.5, sm: 1.75, md: 1.75 },
                  overflow: "visible",
                  "&:last-child": {
                    pb: { xs: 1.5, sm: 1.75, md: 1.75 },
                  },
                }}
              >
                {/* Header row */}
                <Box sx={{
                  display: "flex",
                  alignItems: { xs: "center", sm: "flex-start" },
                  justifyContent: "space-between",
                  mb: { xs: 1.5, sm: 1.5 },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }} onClick={() => setFiltersOpen(false)}>
                    <Box sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: 'var(--primary-soft)',
                      color: 'var(--primary-color)',
                    }}>
                      <SearchIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                      Filters
                    </Typography>
                    <ExpandLessIcon sx={{ fontSize: 20, color: 'var(--text-secondary)' }} />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        const defaultParams = {
                          fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split("T")[0],
                          toDate: new Date().toISOString().split("T")[0]
                        };
                        setProductWiseParams(defaultParams);
                        setProductWisePage(1);
                        setProductWiseData([]);
                      }}
                      sx={{
                        color: 'var(--text-secondary)',
                        fontWeight: 500,
                        fontSize: "13px",
                        textTransform: "none",
                        "&:hover": {
                          background: 'var(--medium-grey)',
                          color: 'var(--text-primary)'
                        }
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                </Box>

                {/* Filter inputs */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    width: "100%",
                    gap: { xs: 1.5, sm: 2 },
                    flexWrap: "wrap",
                    boxSizing: "border-box",

                    "@media (max-width: 700px)": {
                      flexDirection: "column",
                      alignItems: "stretch",
                    },
                  }}
                >
                  {/* FROM DATE */}
                  <Box
                    sx={{
                      flex: "0 1 180px",
                      width: "180px",
                      minWidth: "160px",
                      maxWidth: "200px",

                      "@media (max-width: 700px)": {
                        width: "100%",
                        maxWidth: "100%",
                        flex: "1 1 100%",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.8px",
                        color: 'var(--text-secondary)',
                        mb: "4px",
                      }}
                    >
                      FROM DATE
                    </Typography>

                    <Box
                      sx={{
                        width: "100%",
                        height: 46,
                        boxSizing: "border-box",
                        position: "relative",

                        display: "flex",
                        alignItems: "center",

                        border: '1px solid var(--border-color)',
                        borderRadius: "10px",

                        backgroundColor: 'var(--page-bg)',

                        overflow: "visible",

                        transition: "border-color .2s ease, background-color .2s ease, box-shadow .2s ease",

                        "&:hover": {
                          backgroundColor: 'var(--card-bg)',
                          borderColor: 'var(--medium-grey)',
                        },

                        "&:focus-within": {
                          backgroundColor: 'var(--card-bg)',
                          borderColor: 'var(--primary-color)',
                          boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.12)",
                        },

                        "& input[type='date']": {
                          width: "100%",
                          height: "44px",
                          padding: "0 10px",
                          border: "0",
                          outline: "0",
                          fontSize: "14px",
                          color: 'var(--text-primary)',
                          backgroundColor: "transparent",
                          boxSizing: "border-box",
                          fontFamily: "inherit",
                          minWidth: 0,
                        },

                        "& input[type='date']::-webkit-calendar-picker-indicator": {
                          cursor: "pointer",
                          width: "16px",
                          height: "16px",
                          margin: 0,
                          padding: 0,
                        },
                      }}
                    >
                      <input
                        type="date"
                        value={productWiseParams.fromDate}
                        onChange={(e) =>
                          handleProductWiseDateChange("fromDate", e.target.value)
                        }
                      />
                    </Box>
                  </Box>

                  {/* TO DATE */}
                  <Box
                    sx={{
                      flex: "0 1 180px",
                      width: "180px",
                      minWidth: "160px",
                      maxWidth: "200px",

                      "@media (max-width: 700px)": {
                        width: "100%",
                        maxWidth: "100%",
                        flex: "1 1 100%",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.8px",
                        color: 'var(--text-secondary)',
                        mb: "4px",
                      }}
                    >
                      TO DATE
                    </Typography>

                    <Box
                      sx={{
                        width: "100%",
                        height: 46,
                        boxSizing: "border-box",
                        position: "relative",

                        display: "flex",
                        alignItems: "center",

                        border: '1px solid var(--border-color)',
                        borderRadius: "10px",

                        backgroundColor: 'var(--page-bg)',

                        overflow: "visible",

                        transition: "border-color .2s ease, background-color .2s ease, box-shadow .2s ease",

                        "&:hover": {
                          backgroundColor: 'var(--card-bg)',
                          borderColor: 'var(--medium-grey)',
                        },

                        "&:focus-within": {
                          backgroundColor: 'var(--card-bg)',
                          borderColor: 'var(--primary-color)',
                          boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.12)",
                        },

                        "& input[type='date']": {
                          width: "100%",
                          height: "44px",
                          padding: "0 10px",
                          border: "0",
                          outline: "0",
                          fontSize: "14px",
                          color: 'var(--text-primary)',
                          backgroundColor: "transparent",
                          boxSizing: "border-box",
                          fontFamily: "inherit",
                          minWidth: 0,
                        },

                        "& input[type='date']::-webkit-calendar-picker-indicator": {
                          cursor: "pointer",
                          width: "16px",
                          height: "16px",
                          margin: 0,
                          padding: 0,
                        },
                      }}
                    >
                      <input
                        type="date"
                        value={productWiseParams.toDate}
                        onChange={(e) =>
                          handleProductWiseDateChange("toDate", e.target.value)
                        }
                      />
                    </Box>
                  </Box>

                  {/* ACTION BUTTONS */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 1,
                      marginLeft: "auto",
                      flex: "0 0 auto",
                      minWidth: 0,
                      flexWrap: "nowrap",

                      "@media (max-width: 900px)": {
                        marginLeft: 0,
                        flexWrap: "wrap",
                      },

                      "@media (max-width: 700px)": {
                        width: "100%",
                        marginTop: "4px",
                      },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleProductWisePeriod("WEEKLY")}
                      sx={{
                        height: "32px",
                        minWidth: "82px",
                        px: 1.5,
                        borderRadius: "6px",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "11px",
                        borderColor: 'var(--border-color)',
                        color: 'var(--text-secondary)',
                        whiteSpace: "nowrap",

                        "&:hover": {
                          borderColor: 'var(--primary-color)',
                          background: 'var(--primary-soft)',
                          color: 'var(--primary-color)',
                        },
                      }}
                    >
                      Last Week
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => handleProductWisePeriod("MONTHLY")}
                      sx={{
                        height: "32px",
                        minWidth: "82px",
                        px: 1.5,
                        borderRadius: "6px",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "11px",
                        borderColor: 'var(--border-color)',
                        color: 'var(--text-secondary)',
                        whiteSpace: "nowrap",

                        "&:hover": {
                          borderColor: 'var(--primary-color)',
                          background: 'var(--primary-soft)',
                          color: 'var(--primary-color)',
                        },
                      }}
                    >
                      Last Month
                    </Button>

                    <Button
                      variant="contained"
                      onClick={fetchProductWiseReport}
                      disabled={loading}
                      sx={{
                        height: "32px",
                        minWidth: "60px",
                        px: 1.5,
                        borderRadius: "6px",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "11px",
                        background: 'var(--primary-color)',

                        "&:hover": {
                          background: 'var(--primary-hover)',
                        },
                      }}
                    >
                      {loading ? "..." : "Search"}
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() =>
                        exportToCSV(productWiseData, "product-wise-report.csv")
                      }
                      sx={{
                        height: "32px",
                        minWidth: "60px",
                        px: 1.5,
                        borderRadius: "6px",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "11px",
                        background: "#27AE60",

                        "&:hover": {
                          background: "#219150",
                        },
                      }}
                    >
                      Export
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Product Wise Report Table */}
          {productWiseData.length > 0 && (
            <div style={{ background: 'var(--card-bg)', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', overflow: 'visible', marginBottom: '20px' }}>
              <div style={{ 
                minHeight: 500,
                overflowX: 'auto',
                maxWidth: '100%',
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--medium-grey) transparent'
              }}>
                <table style={{ 
                  width: '100%',
                  minWidth: '800px',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  tableLayout: 'auto'
                }}>
                  <thead style={{ 
                    background: 'var(--page-bg)'
                  }}>
                    <tr>
                      <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 180
                      }}>Product Name</th>
                      <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 120
                      }}>Delivered Qty</th>
                      <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 120
                      }}>Received Qty</th>
                      <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 120
                      }}>Customers</th>
                      <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 80
                      }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productWiseData.map((item, index) => (
                      <tr key={index} style={{ height: 60, hover: { background: 'var(--card-bg)' } }}>
                        <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          fontWeight: 500, 
                          color: 'var(--text-secondary)' 
                        }}>{item.productName}</td>
                        <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          color: 'var(--success)', 
                          fontWeight: 600 
                        }}>{item.deliveredQty}</td>
                        <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          color: 'var(--danger)', 
                          fontWeight: 600 
                        }}>{item.receivedQty}</td>
                        <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle'
                        }}>{item.uniqueCustomers || 0}</td>
                        <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle'
                        }}>
                          <button
                            onClick={() => handleViewProduct(item.productName)}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid var(--primary-color)',
                              borderRadius: '4px',
                              backgroundColor: 'transparent',
                              color: 'var(--primary-color)',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              margin: '0 auto',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                              e.currentTarget.style.color = '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = 'var(--primary-color)';
                            }}
                          >
                            <FaEye size={14} style={{ pointerEvents: 'none' }} />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Box
                sx={{
                  px: { xs: 2, md: 2.5 },
                  py: 2,
                  borderTop: '1px solid var(--border-color)',
                }}
              >
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.875rem', mb: 2 }}>
                  Showing {productWiseData.length > 0 ? ((productWisePage - 1) * productWisePageSize + 1) : 0}–{Math.min(productWisePage * productWisePageSize, productWiseData.length)} of {productWiseData.length} records
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Pagination
                    count={Math.ceil(productWiseData.length / productWisePageSize)}
                    page={productWisePage}
                    onChange={(event, value) => setProductWisePage(value)}
                    disabled={loading}
                    shape="rounded"
                    variant="outlined"
                    color="primary"
                    showFirstButton
                    showLastButton
                    siblingCount={0}
                    boundaryCount={1}
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        transition: "transform .2s, box-shadow .2s, background-color .2s",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 8px rgba(67,56,202,.15)",
                        },
                      },
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={productWisePageSize}
                      onChange={(e) => handleProductWisePageSizeChange(e.target.value)}
                      sx={{
                        height: 36,
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                    >
                      <MenuItem value={5}>5 per page</MenuItem>
                      <MenuItem value={10}>10 per page</MenuItem>
                      <MenuItem value={25}>25 per page</MenuItem>
                      <MenuItem value={50}>50 per page</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </div>
          )}
        </div>
      )}

      {/* Product Wise Inventory Tab */}
      {activeTab === "inventory" && (
        <div>
          {/* Filters */}
          {!filtersOpen ? (
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setFiltersOpen(true)}
              sx={{
                mb: 3,
                height: 48,
                borderRadius: "10px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "14px",
                borderColor: 'var(--border-color)',
                color: 'var(--text-secondary)',
                "&:hover": {
                  borderColor: 'var(--primary-color)',
                  background: 'var(--primary-soft)',
                  color: 'var(--primary-color)',
                },
              }}
            >
              Show Filters
            </Button>
          ) : (
            <Card
              sx={{
                mb: 2,
                borderRadius: "12px",
                border: '1px solid var(--border-color)',
                boxShadow: "0 4px 16px rgba(15,23,42,.05)",
                overflow: "visible",
                background: 'var(--card-bg)',
                position: "relative",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <CardContent
                sx={{
                  px: { xs: 1.5, sm: 2, md: 2 },
                  py: { xs: 1.5, sm: 1.75, md: 1.75 },
                  overflow: "visible",
                  "&:last-child": {
                    pb: { xs: 1.5, sm: 1.75, md: 1.75 },
                  },
                }}
              >
                {/* Header row */}
                <Box sx={{
                  display: "flex",
                  alignItems: { xs: "center", sm: "flex-start" },
                  justifyContent: "space-between",
                  mb: { xs: 1.5, sm: 1.5 },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }} onClick={() => setFiltersOpen(false)}>
                    <Box sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: 'var(--primary-soft)',
                      color: 'var(--primary-color)',
                    }}>
                      <SearchIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                      Filters
                    </Typography>
                    <ExpandLessIcon sx={{ fontSize: 20, color: 'var(--text-secondary)' }} />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        const defaultInventoryParams = {
                          fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split("T")[0],
                          toDate: new Date().toISOString().split("T")[0]
                        };
                        setSelectedProduct("");
                        setInventoryParams(defaultInventoryParams);
                        setDateSortOrder("desc");
                        setInventoryPage(1);
                        setInventoryData([]);
                        setInventoryTotalPages(0);
                        setInventoryTotalElements(0);
                        setInventorySummary({
                          uniqueCustomers: 0,
                          totalChallans: 0,
                          deliveredQty: 0,
                          receivedQty: 0,
                          netQty: 0
                        });
                      }}
                      sx={{
                        color: 'var(--text-secondary)',
                        fontWeight: 500,
                        fontSize: "13px",
                        textTransform: "none",
                        "&:hover": {
                          background: 'var(--medium-grey)',
                          color: 'var(--text-primary)'
                        }
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                </Box>

                {/* Filter inputs */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    width: "100%",
                    gap: { xs: 1.5, sm: 2 },
                    flexWrap: "wrap",
                    boxSizing: "border-box",

                    "@media (max-width: 700px)": {
                      flexDirection: "column",
                      alignItems: "stretch",
                    },
                  }}
                >
                  {/* PRODUCT */}
                  <Box
                    sx={{
                      flex: "0 1 180px",
                      width: "180px",
                      minWidth: "160px",
                      maxWidth: "200px",

                      "@media (max-width: 700px)": {
                        width: "100%",
                        maxWidth: "100%",
                        flex: "1 1 100%",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.8px",
                        color: 'var(--text-secondary)',
                        mb: "4px",
                      }}
                    >
                      PRODUCT
                    </Typography>

                    <AppDropdown
                      value={selectedProduct || ""}
                      options={[
                        { value: "", label: "Search product..." },
                        ...goods.map(good => ({ value: good.name, label: good.name }))
                      ]}
                      onChange={(value) => {
                        setSelectedProduct(value);
                        setInventoryPage(1);
                      }}
                      placeholder="Search product..."
                      position="below"
                    />
                  </Box>

                  {/* FROM DATE */}
                  <Box
                    sx={{
                      flex: "0 1 180px",
                      width: "180px",
                      minWidth: "160px",
                      maxWidth: "200px",

                      "@media (max-width: 700px)": {
                        width: "100%",
                        maxWidth: "100%",
                        flex: "1 1 100%",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.8px",
                        color: 'var(--text-secondary)',
                        mb: "4px",
                      }}
                    >
                      FROM DATE
                    </Typography>

                    <Box
                      sx={{
                        width: "100%",
                        height: 46,
                        boxSizing: "border-box",
                        position: "relative",

                        display: "flex",
                        alignItems: "center",

                        border: '1px solid var(--border-color)',
                        borderRadius: "10px",

                        backgroundColor: 'var(--page-bg)',

                        overflow: "visible",

                        transition: "border-color .2s ease, background-color .2s ease, box-shadow .2s ease",

                        "&:hover": {
                          backgroundColor: 'var(--card-bg)',
                          borderColor: 'var(--medium-grey)',
                        },

                        "&:focus-within": {
                          backgroundColor: 'var(--card-bg)',
                          borderColor: 'var(--primary-color)',
                          boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.12)",
                        },

                        "& input[type='date']": {
                          width: "100%",
                          height: "44px",
                          padding: "0 10px",
                          border: "0",
                          outline: "0",
                          fontSize: "14px",
                          color: 'var(--text-primary)',
                          backgroundColor: "transparent",
                          boxSizing: "border-box",
                          fontFamily: "inherit",
                          minWidth: 0,
                        },

                        "& input[type='date']::-webkit-calendar-picker-indicator": {
                          cursor: "pointer",
                          width: "16px",
                          height: "16px",
                          margin: 0,
                          padding: 0,
                        },
                      }}
                    >
                      <input
                        type="date"
                        value={inventoryParams.fromDate}
                        onChange={(e) =>
                          handleInventoryDateChange("fromDate", e.target.value)
                        }
                      />
                    </Box>
                  </Box>

                  {/* TO DATE */}
                  <Box
                    sx={{
                      flex: "0 1 180px",
                      width: "180px",
                      minWidth: "160px",
                      maxWidth: "200px",

                      "@media (max-width: 700px)": {
                        width: "100%",
                        maxWidth: "100%",
                        flex: "1 1 100%",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.8px",
                        color: 'var(--text-secondary)',
                        mb: "4px",
                      }}
                    >
                      TO DATE
                    </Typography>

                    <Box
                      sx={{
                        width: "100%",
                        height: 46,
                        boxSizing: "border-box",
                        position: "relative",

                        display: "flex",
                        alignItems: "center",

                        border: '1px solid var(--border-color)',
                        borderRadius: "10px",

                        backgroundColor: 'var(--page-bg)',

                        overflow: "visible",

                        transition: "border-color .2s ease, background-color .2s ease, box-shadow .2s ease",

                        "&:hover": {
                          backgroundColor: 'var(--card-bg)',
                          borderColor: 'var(--medium-grey)',
                        },

                        "&:focus-within": {
                          backgroundColor: 'var(--card-bg)',
                          borderColor: 'var(--primary-color)',
                          boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.12)",
                        },

                        "& input[type='date']": {
                          width: "100%",
                          height: "44px",
                          padding: "0 10px",
                          border: "0",
                          outline: "0",
                          fontSize: "14px",
                          color: 'var(--text-primary)',
                          backgroundColor: "transparent",
                          boxSizing: "border-box",
                          fontFamily: "inherit",
                          minWidth: 0,
                        },

                        "& input[type='date']::-webkit-calendar-picker-indicator": {
                          cursor: "pointer",
                          width: "16px",
                          height: "16px",
                          margin: 0,
                          padding: 0,
                        },
                      }}
                    >
                      <input
                        type="date"
                        value={inventoryParams.toDate}
                        onChange={(e) =>
                          handleInventoryDateChange("toDate", e.target.value)
                        }
                      />
                    </Box>
                  </Box>

                  {/* ACTION BUTTONS */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 1,
                      marginLeft: "auto",
                      flex: "0 0 auto",
                      minWidth: 0,
                      flexWrap: "nowrap",

                      "@media (max-width: 900px)": {
                        marginLeft: 0,
                        flexWrap: "wrap",
                      },

                      "@media (max-width: 700px)": {
                        width: "100%",
                        marginTop: "4px",
                      },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => handleInventoryPeriod("WEEKLY")}
                      sx={{
                        height: "32px",
                        minWidth: "82px",
                        px: 1.5,
                        borderRadius: "6px",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "11px",
                        borderColor: 'var(--border-color)',
                        color: 'var(--text-secondary)',
                        whiteSpace: "nowrap",

                        "&:hover": {
                          borderColor: 'var(--primary-color)',
                          background: 'var(--primary-soft)',
                          color: 'var(--primary-color)',
                        },
                      }}
                    >
                      Last Week
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => handleInventoryPeriod("MONTHLY")}
                      sx={{
                        height: "32px",
                        minWidth: "82px",
                        px: 1.5,
                        borderRadius: "6px",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "11px",
                        borderColor: 'var(--border-color)',
                        color: 'var(--text-secondary)',
                        whiteSpace: "nowrap",

                        "&:hover": {
                          borderColor: 'var(--primary-color)',
                          background: 'var(--primary-soft)',
                          color: 'var(--primary-color)',
                        },
                      }}
                    >
                      Last Month
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => fetchProductInventory()}
                      disabled={loading}
                      sx={{
                        height: "32px",
                        minWidth: "60px",
                        px: 1.5,
                        borderRadius: "6px",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "11px",
                        background: 'var(--primary-color)',

                        "&:hover": {
                          background: 'var(--primary-hover)',
                        },
                      }}
                    >
                      {loading ? "..." : "Search"}
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() =>
                        exportToCSV(inventoryData, "product-inventory-report.csv")
                      }
                      sx={{
                        height: "32px",
                        minWidth: "60px",
                        px: 1.5,
                        borderRadius: "6px",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "11px",
                        background: "#27AE60",

                        "&:hover": {
                          background: "#219150",
                        },
                      }}
                    >
                      Export
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Product Inventory Table */}
          {inventoryData.length > 0 && (
            <>
              {/* Summary Cards */}
              <div style={{ marginBottom: '16px', background: 'var(--card-bg)', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '16px',
                  padding: '16px'
                }}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Product</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>{selectedProduct}</div>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Unique Customers</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>{inventorySummary.uniqueCustomers}</div>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Challans</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>{inventorySummary.totalChallans}</div>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Delivered Qty</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--success)' }}>{inventorySummary.deliveredQty}</div>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Received Qty</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--danger)' }}>{inventorySummary.receivedQty}</div>
                  </div>
                  <div style={{
                    padding: '12px',
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Net Qty</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: inventorySummary.netQty >= 0 ? 'var(--success)' : 'var(--danger)' }}>{inventorySummary.netQty}</div>
                  </div>
                </div>
              </div>

              <div style={{ background: 'var(--card-bg)', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', overflow: 'visible' }}>
                <div style={{ 
                minHeight: 500,
                overflowX: 'auto',
                maxWidth: '100%',
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--medium-grey) transparent'
              }}>
                  <table style={{ 
                  width: '100%',
                  minWidth: '800px',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  tableLayout: 'auto'
                }}>
                    <thead style={{ 
                    background: 'var(--page-bg)'
                  }}>
                      <tr>
                        <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 120
                      }}>Date</th>
                        <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 180
                      }}>Customer Name</th>
                        <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 120
                      }}>Challan No</th>
                        <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 120
                      }}>Challan Type</th>
                        <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 100
                      }}>Delivered Qty</th>
                        <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 100
                      }}>Received Qty</th>
                        <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 120
                      }}>Combined Delivered</th>
                        <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 120
                      }}>Combined Received</th>
                        <th style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontWeight: 700, 
                        backgroundColor: 'var(--page-bg)', 
                        fontSize: 12,
                        width: 80
                      }}>Net Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventoryData.map((item, index) => (
                        <tr key={index} style={{ height: 60, hover: { background: 'var(--card-bg)' } }}>
                          <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          color: 'var(--text-secondary)'
                        }}>{formatDate(item.date)}</td>
                          <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          fontWeight: 500, 
                          color: 'var(--text-secondary)' 
                        }}>{item.customerName}</td>
                          <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          fontWeight: 500, 
                          color: 'var(--text-secondary)' 
                        }}>{item.challanNo}</td>
                          <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle'
                        }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '20px',
                              fontSize: '10px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              backgroundColor: item.challanType === 'DELIVERED' ? 'rgba(34,197,94,.16)' : item.challanType === 'RECEIVED' ? 'rgba(239,68,68,.16)' : 'rgba(245,158,11,.16)',
                              color: item.challanType === 'DELIVERED' ? 'var(--success)' : item.challanType === 'RECEIVED' ? 'var(--danger)' : 'var(--warning)'
                            }}>
                              {item.challanType}
                            </span>
                          </td>
                          <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          color: 'var(--success)', 
                          fontWeight: 600 
                        }}>{item.deliveredQty}</td>
                          <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          color: 'var(--danger)', 
                          fontWeight: 600 
                        }}>{item.receivedQty}</td>
                          <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          color: 'var(--warning)', 
                          fontWeight: 600 
                        }}>{item.combinedDeliveredQty}</td>
                          <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          color: 'var(--warning)', 
                          fontWeight: 600 
                        }}>{item.combinedReceivedQty}</td>
                          <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          verticalAlign: 'middle',
                          fontWeight: 600, 
                          color: getNetQty(item) > 0 ? '#27ae60' : '#e74c3c' 
                        }}>
                            {getNetQty(item)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination for Product Wise Inventory */}
                <Box
                  sx={{
                    px: { xs: 2, md: 2.5 },
                    py: 2,
                    borderTop: '1px solid var(--border-color)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.875rem', mb: 2 }}>
                    Showing {inventoryTotalElements > 0 ? ((inventoryPage - 1) * inventoryPageSize + 1) : 0}–{Math.min(inventoryPage * inventoryPageSize, inventoryTotalElements)} of {inventoryTotalElements} records
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Pagination
                      count={inventoryTotalPages}
                      page={inventoryPage}
                      onChange={(event, value) => setInventoryPage(value)}
                      disabled={loading}
                      shape="rounded"
                      variant="outlined"
                      color="primary"
                      showFirstButton
                      showLastButton
                      siblingCount={0}
                      boundaryCount={1}
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          transition: "transform .2s, box-shadow .2s, background-color .2s",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: "0 2px 8px rgba(67,56,202,.15)",
                          },
                        },
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={inventoryPageSize}
                        onChange={(e) => handleInventoryPageSizeChange(e.target.value)}
                        sx={{
                          height: 36,
                          borderRadius: '8px',
                          fontSize: '13px',
                        }}
                      >
                        <MenuItem value={5}>5 per page</MenuItem>
                        <MenuItem value={10}>10 per page</MenuItem>
                        <MenuItem value={25}>25 per page</MenuItem>
                        <MenuItem value={50}>50 per page</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </div>
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && (
        (activeTab === "productwise" && productWiseData.length === 0) ||
        (activeTab === "inventory" && inventoryData.length === 0)
      ) && (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--card-bg)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px', margin: 0 }}>No data to display. Try adjusting your filters.</p>
        </div>
      )}
      </div>
    </div>
  );
};

export default Reports;
